import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signIn } from '../lib/auth';
// import { signInWithSocial } from '../lib/auth';
import { useAuth } from '@/hooks/useAuth';
import Footer from '../components/ui/Footer';
import LanguageSelector from '@/components/ui/LanguageSelector';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [socialLoading, setSocialLoading] = useState(false);

  // function handleSocialLogin(provider: 'Google' | 'Facebook') {
  //   setSocialLoading(true);
  //   try {
  //     signInWithSocial(provider);
  //     // Redirect happens automatically
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : t('login.errors.authFailed'));
  //     setSocialLoading(false);
  //   }
  // }

  useEffect(() => {
    // Wait for auth state to be loaded, then redirect if already authenticated
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      // Update auth context - useEffect will handle navigation when user state updates
      await refreshUser();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('login.errors.invalidCredentials')
      );
    } finally {
      setLoading(false);
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mk-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header bar */}
      <header className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="text-xl font-bold">
              <span className="text-white">MyPink</span>
              <span className="text-mk-pink-400">Contact</span>
            </Link>

            {/* Right side */}
            <LanguageSelector variant="light" />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <p className="text-gray-600">{t('login.subtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Social Login Buttons - Disabled */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                disabled={true}
                className="w-full flex items-center justify-center gap-3 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleIcon />
                {t('login.continueGoogle')}
              </button>
              <button
                type="button"
                disabled={true}
                className="w-full flex items-center justify-center gap-3 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FacebookIcon />
                {t('login.continueFacebook')}
              </button>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  {t('login.orEmail')}
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('login.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                  placeholder={t('login.emailPlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('login.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                  placeholder={t('login.passwordPlaceholder')}
                  required
                />
                <div className="mt-1 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-mk-pink-600 hover:text-mk-pink-700"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              {t('login.noAccount')}{' '}
              <Link
                to="/register"
                className="text-mk-pink-600 hover:text-mk-pink-700 font-medium"
              >
                {t('login.signUp')}
              </Link>
            </p>
          </div>

        </div>
      </div>

      {/* Footer */}
      <Footer showCta={false} />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
